import { api } from '../client'
import type { JobDoc } from './uploads.api'

export interface FileDoc {
  _id: string
  title: string
  description?: string
  fileName: string
  fileType: 'csv' | 'geojson' | 'shapefile' | 'geopackage' | 'gdb'
  fileSize: number
  s3Bucket: string
  s3Key: string
  uploadStatus: 'uploading' | 'uploaded' | 'upload_failed'
  publishStatus?: 'publishing' | 'published' | 'publish_failed'
  publishJobId?: string
  publishedLayerId?: string
  publishError?: string
  publishedAt?: string
  sharingLevel: string
  sharedGroupIds?: string[]
  status: string
  ownedBy: string
  createdBy: string
  updatedBy?: string
  createdAt: string
  updatedAt: string
}

export interface FilesListResponse {
  data: FileDoc[]
  pagination: { limit: number; cursor: string | null; hasMore: boolean }
}

export interface FilesListParams {
  limit?: number
  cursor?: string
  fileType?: string
  uploadStatus?: string
  publishStatus?: string
}

export interface FileCreateInput {
  title: string
  description?: string
  fileName: string
  fileType: string
  fileSize: number
  s3Bucket: string
  s3Key: string
  sharingLevel?: string
}

export interface FilePublishInput {
  serverId: string
  schema?: string
  tableName: string
  /** Defaults to 'create' server-side when omitted. */
  intent?: 'create' | 'replace'
  /** Required when `intent` is 'replace' — the Layer/CsvDataTable being replaced. */
  replaceLayerId?: string
  /** Acknowledges the column changes a replace makes. API callers only: the console confirms with `checkJobId`. */
  confirmSchemaChanges?: boolean
  /** Acknowledges that forms collect into the layer a replace overwrites (#1547). API callers only, as above. */
  confirmBoundForms?: boolean
  /**
   * Dry run of a replace (#1547): the handler runs every gate but reserves no credit and leaves the File's
   * publish status alone; the job ends `done` with `result.check` (a `ReplaceCheckResult`). Only valid with
   * `intent: 'replace'`, and never together with `checkJobId`.
   */
  check?: boolean
  /**
   * The `_id` of a `done` check job (24 hex characters) for the same file, layer and caller, under an hour
   * old. The run then counts as confirmed for both the bound forms and the column changes. A difference
   * since the check is refused with `replace_check_stale`: a 409 here, or a failed job with that `error.code`.
   */
  checkJobId?: string
  options?: Record<string, unknown>
}

/** A form that writes its responses into the layer a replace would overwrite. */
export interface BoundFormSummary {
  id: string
  name: string
  status: 'draft' | 'published' | string
  /** Responses the form has written into the layer. */
  committedResponses: number
}

/**
 * What a replace asks the user to confirm. The handler sends `boundForms` (#1547); the column
 * fields are the worker's `SCHEMA_CHANGE_REQUIRES_CONFIRMATION` details, or a client pre-flight's.
 */
export interface ReplaceConfirmationDetails {
  boundForms?: BoundFormSummary[]
  /** Every bound form, including any past the listing cap. */
  boundFormCount?: number
  added?: string[]
  removed?: string[]
  retyped?: Array<{ column: string; from: string; to: string }>
  /** The geometry type, its Z/M dimensions or its SRID changes (`null` = the side has no geometry). */
  geometryChanged?: { from: string | null; to: string | null }
  addedChildren?: string[]
  removedChildren?: string[]
  schemaUnreadable?: boolean
}

/** `POST /files/{id}/publish` answering a question instead of starting a job. */
export interface ReplaceConfirmationRequest {
  requiresConfirmation: true
  code: string
  message: string
  details: ReplaceConfirmationDetails
}

export type FilePublishResult = JobDoc | ReplaceConfirmationRequest

export function isReplaceConfirmationRequest(data: unknown): data is ReplaceConfirmationRequest {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { requiresConfirmation?: unknown }).requiresConfirmation === true
  )
}

/**
 * The machine codes a replace is refused with: `ApiRequestError.code` on a handler refusal, or a failed
 * job's `error.code`. Mirrors `REPLACE_CODES` in libs/feature-targets/src/lib/publish/replace-check.ts.
 */
export const REPLACE_CODES = {
  linkedLayer: 'replace_linked_layer',
  notSupported: 'replace_not_supported',
  missingSource: 'replace_missing_source',
  checkStale: 'replace_check_stale',
} as const

export type ReplaceCode = (typeof REPLACE_CODES)[keyof typeof REPLACE_CODES]

/**
 * What a replace CHECK found, before anything was written (`job.result.check`). Mirrors
 * `ReplaceCheckResult` in libs/feature-targets/src/lib/publish/replace-check.ts (the console cannot
 * import that barrel; keep the two in step).
 */
export interface ReplaceCheckResult {
  /** Forms that collect into the layer: replacing deletes the rows they collected. At most 50, by name. */
  boundForms: BoundFormSummary[]
  /** Every bound form, including any past the listed 50: the summary says "+N more" for the rest. */
  boundFormCount: number
  added: string[]
  removed: string[]
  retyped: Array<{ name: string; from: string; to: string }>
  geometryChanged: { from: string | null; to: string | null } | null
  /** Layer groups: tables new in the file / child layers whose table is gone from it. */
  addedChildren: string[]
  removedChildren: string[]
  /** Columns the layer's style refers to that the new data no longer has. */
  styleColumnsRemoved: string[]
  /** null when the engine could not give an authoritative count. */
  featureCount: { before: number | null; after: number | null }
  /** Identifies this diff; the real run is refused as stale when its own diff hashes differently. */
  diffHash: string
}

export interface FileUpdateInput {
  title?: string
  description?: string
  sharingLevel?: string
  /** Modern field — exclusive sharing model with sharingLevel='groups' (sub-project 2). */
  sharedWithGroups?: string[]
  /** Legacy field — kept for backwards compatibility with older callers. */
  sharedGroupIds?: string[]
}

export const filesApi = {
  create: (data: FileCreateInput) => api.post<FileDoc>('/files', data),

  list: (params?: FilesListParams) =>
    api.get<FilesListResponse>('/files', {
      params: params as Record<string, string | number | undefined>,
    }),

  get: (id: string) => api.get<FileDoc>(`/files/${id}`),

  update: (id: string, data: FileUpdateInput) => api.patch<FileDoc>(`/files/${id}`, data),

  delete: (id: string) => api.delete<{ success: boolean; message: string }>(`/files/${id}`),

  /** Resolves with the queued job, or — for a replace that must be confirmed — the question. */
  publish: (id: string, data: FilePublishInput) => api.post<FilePublishResult>(`/files/${id}/publish`, data),
}
